import React from "react";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  closeDetailModal,
  getIsActivityLogDetailOpen,
  getSelectedActivityLog,
} from "../../features/activityLogs/activityLogsSlice";
import { ActivityLogProps } from "../../types/activityLog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { t } from "../../locales";

const eventBadgeClass = (event: string) => {
  switch (event) {
    case "created":
      return "bg-emerald-600 hover:bg-emerald-700 text-white";
    case "updated":
      return "bg-blue-600 hover:bg-blue-700 text-white";
    case "deleted":
      return "bg-rose-600 hover:bg-rose-700 text-white";
    default:
      return "bg-gray-500 hover:bg-gray-600 text-white";
  }
};

const eventLabel = (event: string) => {
  const map: Record<string, string> = {
    created: (t.activityLogs as any).eventCreated ?? "Created",
    updated: (t.activityLogs as any).eventUpdated ?? "Updated",
    deleted: (t.activityLogs as any).eventDeleted ?? "Deleted",
  };
  return map[event] ?? event;
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

const KeyValueRow: React.FC<{ k: string; oldValue: any; newValue: any; isUpdate: boolean; isCreate: boolean; isDelete: boolean }> = ({
  k,
  oldValue,
  newValue,
  isUpdate,
  isCreate,
  isDelete,
}) => {
  if (isCreate) {
    return (
      <tr className="border-t border-gray-100">
        <td className="px-3 py-2 text-sm font-medium text-gray-700 align-top w-1/3">{k}</td>
        <td className="px-3 py-2 text-sm bg-emerald-50 text-emerald-900 align-top whitespace-pre-wrap break-all">
          {formatValue(newValue)}
        </td>
      </tr>
    );
  }
  if (isDelete) {
    return (
      <tr className="border-t border-gray-100">
        <td className="px-3 py-2 text-sm font-medium text-gray-700 align-top w-1/3">{k}</td>
        <td className="px-3 py-2 text-sm bg-rose-50 text-rose-900 align-top whitespace-pre-wrap break-all">
          {formatValue(oldValue)}
        </td>
      </tr>
    );
  }
  const changed = formatValue(oldValue) !== formatValue(newValue);
  return (
    <tr className="border-t border-gray-100">
      <td className="px-3 py-2 text-sm font-medium text-gray-700 align-top w-1/4">{k}</td>
      <td className={`px-3 py-2 text-sm align-top whitespace-pre-wrap break-all ${changed ? "bg-rose-50 text-rose-900" : "text-gray-500"}`}>
        {formatValue(oldValue)}
      </td>
      <td className={`px-3 py-2 text-sm align-top whitespace-pre-wrap break-all ${changed ? "bg-emerald-50 text-emerald-900" : "text-gray-500"}`}>
        {formatValue(newValue)}
      </td>
    </tr>
  );
};

const renderDiffTable = (log: ActivityLogProps) => {
  const labels = t.activityLogs as any;
  const isCreate = log.event === "created";
  const isDelete = log.event === "deleted";
  const isUpdate = log.event === "updated";

  const oldData = log.old_data ?? {};
  const newData = log.new_data ?? {};
  const allKeys = Array.from(
    new Set([...Object.keys(oldData), ...Object.keys(newData)])
  ).sort();

  if (allKeys.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-3">{labels.noFieldData ?? "No field data captured."}</p>
    );
  }

  if (isUpdate) {
    return (
      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                {labels.field ?? "Field"}
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                {labels.oldValue ?? "Old Value"}
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                {labels.newValue ?? "New Value"}
              </th>
            </tr>
          </thead>
          <tbody>
            {allKeys.map((k) => (
              <KeyValueRow
                key={k}
                k={k}
                oldValue={oldData[k]}
                newValue={newData[k]}
                isUpdate={isUpdate}
                isCreate={isCreate}
                isDelete={isDelete}
              />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded border border-gray-200">
      <table className="min-w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
              {labels.field ?? "Field"}
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
              {isCreate ? labels.newValue ?? "New Value" : labels.oldValue ?? "Old Value"}
            </th>
          </tr>
        </thead>
        <tbody>
          {allKeys.map((k) => (
            <KeyValueRow
              key={k}
              k={k}
              oldValue={oldData[k]}
              newValue={newData[k]}
              isUpdate={isUpdate}
              isCreate={isCreate}
              isDelete={isDelete}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ActivityLogDetailModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const open = useAppSelector(getIsActivityLogDetailOpen);
  const log = useAppSelector(getSelectedActivityLog);
  const labels = t.activityLogs as any;

  const handleOpenChange = (next: boolean) => {
    if (!next) dispatch(closeDetailModal());
  };

  return (
    <Dialog open={open && !!log} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#113F67]">
            {labels.modalTitle ?? "Activity Detail"}
          </DialogTitle>
          {log ? (
            <DialogDescription>
              {log.model_label} #{log.model_id} ·{" "}
              {moment(log.created_at).format("DD MMM YYYY, hh:mm:ss A")}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        {log ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="text-xs uppercase font-semibold text-gray-500 mb-1">
                  {labels.metaModule ?? "Module"}
                </p>
                <p className="text-sm text-gray-900">
                  {log.model_label} <span className="text-gray-500">#{log.model_id}</span>
                </p>
              </div>
              <div>
                <p className="text-xs uppercase font-semibold text-gray-500 mb-1">
                  {labels.metaEvent ?? "Event"}
                </p>
                <Badge className={eventBadgeClass(log.event)}>{eventLabel(log.event)}</Badge>
              </div>
              <div>
                <p className="text-xs uppercase font-semibold text-gray-500 mb-1">
                  {labels.metaUser ?? "User"}
                </p>
                <p className="text-sm text-gray-900">
                  {log.user?.name ?? labels.systemUser ?? "System"}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase font-semibold text-gray-500 mb-1">
                  {labels.metaTimestamp ?? "Timestamp"}
                </p>
                <p className="text-sm text-gray-900">
                  {moment(log.created_at).format("DD MMM YYYY, hh:mm:ss A")}
                </p>
              </div>
              {log.ip_address ? (
                <div>
                  <p className="text-xs uppercase font-semibold text-gray-500 mb-1">
                    {labels.metaIp ?? "IP Address"}
                  </p>
                  <p className="text-sm text-gray-900">{log.ip_address}</p>
                </div>
              ) : null}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                {labels.fieldChanges ?? "Field Changes"}
              </h3>
              {renderDiffTable(log)}
            </div>

            <div className="flex justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => dispatch(closeDetailModal())}>
                {labels.closeButton ?? "Close"}
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default ActivityLogDetailModal;
